import { useEffect, useRef } from 'react';
import { ChatMessage } from '../types';

// Réplique l'URL utilisée par apiService — l'API tourne en relatif depuis le frontend
const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

export type ChatStreamEventType = 'created' | 'edited' | 'deleted' | 'ping' | 'ready' | 'bye';

export interface ChatStreamEvent {
  type: ChatStreamEventType;
  message?: ChatMessage;
}

type Handler = (event: ChatStreamEvent) => void;

/**
 * Hook SSE pour le chat — Phase 3.
 *
 * Ouvre une connexion long-poll (fetch + ReadableStream) vers chat_stream
 * et appelle `onEvent` à chaque message reçu. La connexion se rouvre
 * automatiquement à chaque fin de cycle serveur (~30 s) et après une
 * coupure réseau (backoff exponentiel léger).
 *
 * On utilise fetch+ReadableStream et non EventSource natif car EventSource
 * ne supporte pas les headers custom (Authorization Bearer).
 *
 * Le hook est résilient : si le réseau tombe, il retente en boucle tant
 * que le composant est monté et que conversationId est défini.
 */
export function useChatStream(conversationId: string | null, onEvent: Handler): void {
  // Ref pour ne pas relancer la connexion à chaque render quand onEvent change
  const handlerRef = useRef<Handler>(onEvent);
  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!conversationId) return;
    const controller = new AbortController();
    let cancelled = false;
    let backoffMs = 1000;

    const parseSseBuffer = (buf: string): { events: ChatStreamEvent[]; rest: string } => {
      const events: ChatStreamEvent[] = [];
      let idx: number;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        let eventType: ChatStreamEventType | null = null;
        let dataLine: string | null = null;
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.substring(7).trim() as ChatStreamEventType;
          else if (line.startsWith('data: ')) dataLine = line.substring(6);
        }
        if (eventType && dataLine !== null) {
          try {
            const payload = JSON.parse(dataLine);
            events.push({ type: eventType, message: payload.message });
          } catch { /* malformé — on ignore */ }
        }
      }
      return { events, rest: buf };
    };

    const connect = async (): Promise<void> => {
      while (!cancelled && !controller.signal.aborted) {
        try {
          const token = sessionStorage.getItem('orion_access_token') || '';
          const res = await fetch(
            `${API_BASE}?action=chat_stream&conversation_id=${encodeURIComponent(conversationId)}`,
            {
              method: 'GET',
              headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
              signal: controller.signal,
              credentials: 'include',
            }
          );

          if (!res.ok || !res.body) {
            // 401, 403, 5xx : on backoff
            throw new Error(`HTTP ${res.status}`);
          }

          // Connexion établie → reset backoff
          backoffMs = 1000;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (!cancelled) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const { events, rest } = parseSseBuffer(buffer);
            buffer = rest;
            for (const ev of events) {
              try { handlerRef.current(ev); } catch { /* l'app ne doit pas crasher */ }
            }
          }
        } catch (err) {
          if (cancelled || controller.signal.aborted) return;
          // Retente avec backoff exponentiel (max 10 s)
          await new Promise<void>(r => setTimeout(r, backoffMs));
          backoffMs = Math.min(backoffMs * 2, 10000);
        }
      }
    };

    connect();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [conversationId]);
}
